import { formatDate } from '@telerik/kendo-intl';
import { Render } from 'Components/Display';
import { useEnums } from 'Context';
import { NamedCoreEnum } from 'Models';
import { CONTROLLERS } from 'Models/Templates/Routes/Routes';
import React, { Fragment, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useDataFetcher, useLineOfBusinessParam } from 'Utilities';
import { parseUtcToLocal } from 'Utilities/DateTimeUtc';
import { DateTimePickerInput } from 'v3/Components/Form/DateTimePickers/DateTimePicker';
import Dropdown, {
  DropdownDbOption,
  DropdownOption,
} from 'v3/Components/Form/Dropdown';
import { FieldError } from 'v3/Components/Form/FormSection';
import { InputActive } from 'v3/Components/Form/InputActive';
import ReadOnlyField from 'v3/Components/Form/ReadOnlyField';
import { TitleActive } from 'v3/Components/Form/TitleActive';
import { TitleReadOnly } from 'v3/Components/Form/TitleReadOnly';
import { LineOfBusinessEnum } from 'v3/Models/Enums';
import { ReplicableComponent } from '../../Form/ReplicableComponent';
import './AddEditReferralsRequest.AdditionalInformation.scss';
import './AddEditReferralsRequest.ReportDetails.scss';
import {
  createReferralRequestSchema,
  useReferralRequestForm,
} from './useReferralRequestForm';

type ReportDetailsProps = {
  form: ReturnType<typeof useReferralRequestForm>;
  fieldErrors?: FieldError[];
  setFieldError?: (fieldId: string, error: string) => void;
};

export const ReportDetailsActive: React.FC<ReportDetailsProps> = ({
  form,
  fieldErrors,
  setFieldError,
}) => {
  const { lob } = useLineOfBusinessParam();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const clientId = searchParams.get('clientId');
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();
  const { specialty, tatPriority, state, apContact } = useEnums();
  const [services, servicesStatus] = useDataFetcher<DropdownDbOption[]>(
    `${CONTROLLERS.Users}/servicesByLineOfBusinessFilteredByClient?${
      clientId != null ? 'clientId=' + clientId : ''
    }
    &lineOfBusiness=${lob}`,
    [],
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const [subServices, subServicesStatus] = useDataFetcher<DropdownDbOption[]>(
    `${CONTROLLERS.Users}/subServicesByLineOfBusiness?${
      clientId != null ? 'clientId=' + clientId : ''
    }&lineOfBusiness=${lob}`,
    [],
    (d) => d.data,
    undefined,
    true,
    '3.0'
  );

  const [tats, tatsStatus] = useDataFetcher<NamedCoreEnum>(
    `${CONTROLLERS.Users}/tatsByLineOfBusiness?${
      clientId != null ? 'clientId=' + clientId : ''
    }&lineOfBusiness=${lob}&serviceId=${
      form.getFieldProps('service').value?.id
    }`,
    {},
    (d) => d.data,
    undefined,
    form.getFieldProps('service').value?.id !== undefined,
    '3.0'
  );

  const isApRequired = useMemo(() => {
    return (
      form.getFieldProps('apContactPreference')?.value?.value ===
        apContact['Required'].value ||
      form.getFieldProps('apContactPreference')?.value?.value ===
        apContact['AtDiscretion'].value ||
      form.getFieldProps('apContactPreference')?.value?.value ===
        apContact['OnDenial'].value
    );
  }, [form.getFieldProps('apContactPreference')?.value?.value]);

  const attendingProviders = form.getFieldProps('attendingProviders').value;

  const tatOpened = () => {
    if (!form.getFieldProps('service').value) {
      setFieldError &&
        setFieldError(
          'tat',
          'Service must be selected before TAT options are available'
        );
    }
  };

  return (
    <div className="">
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleActive>Requirements</TitleActive>
        <div className="active-row">
          <div className="active-fields">
            <Dropdown
              label="Service"
              required
              value={form.getFieldProps('service')?.value}
              onChange={(value) => form.setFieldValue('service', value)}
              dbOptions={servicesStatus === 'success' ? services : []}
              error={fieldErrors?.find((f) => f.fieldId === 'service')?.error}
              sortAlphabetically
              showIcons={false}
            />
            <Dropdown
              label="Sub-Service"
              required
              value={form.getFieldProps('subService').value}
              onChange={(value) => form.setFieldValue('subService', value)}
              dbOptions={subServicesStatus === 'success' ? subServices : []}
              error={
                fieldErrors?.find((f) => f.fieldId === 'subService')?.error
              }
              sortAlphabetically
              showIcons={false}
            />
            <div className="empty-container"></div>
          </div>
        </div>
        <div className="active-row">
          <div className="active-fields">
            <Render condition={!isIME}>
              <Dropdown
                label="Jurisdictional Match Required"
                hint="This determines if the referral needs a state-licensed reviewer"
                required
                value={
                  form.getFieldProps('jurisditionalMatchRequired')?.value
                    ? { displayName: 'Yes', value: 1 }
                    : form.getFieldProps('jurisditionalMatchRequired')
                        ?.value === false
                    ? { displayName: 'No', value: 0 }
                    : undefined
                }
                onChange={(value) =>
                  form.setFieldValue(
                    'jurisditionalMatchRequired',
                    value?.value === 1
                  )
                }
                options={[
                  { displayName: 'Yes', value: 1 },
                  { displayName: 'No', value: 0 },
                ]}
                error={
                  fieldErrors?.find(
                    (f) => f.fieldId === 'jurisditionalMatchRequired'
                  )?.error
                }
              />
            </Render>
            <Dropdown
              label="State of Jurisdiction"
              required
              value={form.getFieldProps('benefitState').value}
              onChange={(value) => form.setFieldValue('benefitState', value)}
              options={[]}
              enums={state}
              valueAsDisplayName
              error={
                fieldErrors?.find((f) => f.fieldId === 'benefitState')?.error
              }
            />

            <Dropdown
              label="Number of Reviewer Specialties"
              value={form.getFieldProps('numberOfReviews').value}
              onChange={(value) => {
                form.setFieldValue('numberOfReviews', value);
                const currentValues =
                  form.getFieldProps('requestedReviewerSpecialties').value ||
                  [];
                const newValues = [...currentValues];
                if (typeof value.value !== 'number') {
                  form.setFieldValue('requestedReviewerSpecialties', []);
                  return;
                }
                if (newValues.length < value.value) {
                  for (let i = newValues.length; i < value.value; i++) {
                    newValues[i] = { value: null, displayName: '' };
                  }
                } else {
                  newValues.splice(value.value, newValues.length - value.value);
                }
                form.setFieldValue('requestedReviewerSpecialties', [
                  ...newValues,
                ]);
              }}
              options={[
                { displayName: '1', value: 1 },
                { displayName: '2', value: 2 },
                { displayName: '3', value: 3 },
                { displayName: '4', value: 4 },
                { displayName: '5', value: 5 },
              ]}
              error={
                fieldErrors?.find((f) => f.fieldId === 'numberOfReviews')?.error
              }
            />
            <Render
              condition={
                isIME ||
                !form?.getFieldProps('jurisditionalMatchRequired')?.value
              }
            >
              <div className="input-container"></div>
            </Render>
          </div>
        </div>
        <div className="active-row">
          <div className="specialties">
            {form?.getFieldProps('numberOfReviews')?.value?.value &&
              [
                ...Array(form?.getFieldProps('numberOfReviews')?.value?.value),
              ].map((_, index) => (
                <Dropdown
                  key={index}
                  label={`Requested Reviewer Specialty ${index + 1}`}
                  hint="This is the desired reviewer specialty for this referral"
                  value={
                    form.getFieldProps(`requestedReviewerSpecialties[${index}]`)
                      .value
                  }
                  onChange={(value) => {
                    const currentValues =
                      form.getFieldProps('requestedReviewerSpecialties')
                        .value || [];
                    currentValues[index] = value;
                    form.setFieldValue('requestedReviewerSpecialties', [
                      ...currentValues,
                    ]);
                  }}
                  options={[]}
                  enums={specialty}
                  error={
                    fieldErrors?.find(
                      (f) =>
                        f.fieldId === `requestedReviewerSpecialties[${index}]`
                    )?.error
                  }
                />
              ))}
          </div>
        </div>
        <TitleActive>Date</TitleActive>
        <div className="active-row">
          <div className="active-fields">
            <DateTimePickerInput
              label={'Due Date & Time'}
              value={form.getFieldProps('dueDate').value}
              required={!isIME}
              onChange={(value: any) => {
                form.setFieldValue('dueDate', value);
              }}
              error={fieldErrors?.find((f) => f.fieldId === 'dueDate')?.error}
            />
            <Dropdown
              label="TAT (Turn Around Time)"
              value={form.getFieldProps('tat').value}
              onChange={(value) => form.setFieldValue('tat', value)}
              enums={tatsStatus === 'success' ? tats : undefined}
              error={fieldErrors?.find((f) => f.fieldId === 'tat')?.error}
              onOpen={tatOpened}
            />
            <Dropdown
              label="TAT Priority"
              hint="This priority level is tied to the request in the TAT field"
              value={form.getFieldProps('tatPriority').value}
              onChange={(value) => form.setFieldValue('tatPriority', value)}
              options={[]}
              enums={tatPriority}
              error={
                fieldErrors?.find((f) => f.fieldId === 'tatPriority')?.error
              }
            />
          </div>
        </div>

        <TitleActive>Attending Provider(s)</TitleActive>
        <div className="active-row">
          <div className="active-fields">
            <Dropdown
              label="AP Contact Preference"
              required
              value={form.getFieldProps('apContactPreference').value}
              onChange={(value) =>
                form.setFieldValue('apContactPreference', value)
              }
              options={[]}
              enums={apContact}
              error={
                fieldErrors?.find((f) => f.fieldId === 'apContactPreference')
                  ?.error
              }
            />
            <div className="input-container"></div>
            <div className="input-container"></div>
          </div>
        </div>
        <TitleActive>Attending Provider Information</TitleActive>

        <div className="active-row">
          <ReplicableComponent
            form={form}
            label={'Attending Provider(s)'}
            formPropsToClearOnRemoval={['attendingProviders']}
            initialDataArray={attendingProviders}
            initialData={{
              apName: '',
              apSpecialty: '',
              apPhoneNumber: '',
              apEmail: '',
            }}
          >
            {({ index, data, onChange, onDuplicate }) => (
              <div className="attending-providers">
                <InputActive
                  id="apName"
                  name="apName"
                  label="AP Name"
                  required={isApRequired}
                  value={
                    form.getFieldProps('attendingProviders').value?.[index]
                      ?.apName ?? ''
                  }
                  onChange={(e: any) => {
                    onChange({ apName: e.target.value }, 'attendingProviders');
                  }}
                  error={
                    fieldErrors?.find(
                      (f) => f.fieldId === `attendingProviders[${index}].APName`
                    )?.error
                  }
                />
                <Dropdown
                  label="AP Specialty"
                  value={
                    form.getFieldProps('attendingProviders').value?.[index]
                      ?.apSpecialty ?? ''
                  }
                  onChange={(value: DropdownOption) => {
                    onChange({ apSpecialty: value }, 'attendingProviders');
                  }}
                  required={isApRequired}
                  options={[]}
                  enums={specialty}
                  error={
                    fieldErrors?.find(
                      (f) =>
                        f.fieldId === `attendingProviders[${index}].APSpecialty`
                    )?.error
                  }
                />
                <InputActive
                  id="apPhoneNumber"
                  name="apPhoneNumber"
                  label="AP Phone Number"
                  value={
                    form.getFieldProps('attendingProviders').value?.[index]
                      ?.apPhoneNumber ?? ''
                  }
                  onChange={(e: any) => {
                    onChange(
                      { apPhoneNumber: e.target.value },
                      'attendingProviders'
                    );
                  }}
                  required={isApRequired}
                  error={
                    fieldErrors?.find(
                      (f) =>
                        f.fieldId ===
                        `attendingProviders[${index}].APPhoneNumber`
                    )?.error
                  }
                />
                <InputActive
                  id="apEmail"
                  name="apEmail"
                  label="AP Fax or Email"
                  value={
                    form.getFieldProps('attendingProviders').value?.[index]
                      ?.apEmail ?? ''
                  }
                  onChange={(e: any) => {
                    onChange({ apEmail: e.target.value }, 'attendingProviders');
                  }}
                  error={
                    fieldErrors?.find(
                      (f) =>
                        f.fieldId === `attendingProviders[${index}].APEmail`
                    )?.error
                  }
                />
              </div>
            )}
          </ReplicableComponent>
        </div>
      </Render>
    </div>
  );
};

export const ReportDetailsReadOnly: React.FC<ReportDetailsProps> = ({
  form,
}) => {
  const { lob } = useLineOfBusinessParam();
  const ReferralRequestSchema = useMemo(
    () => createReferralRequestSchema(Number(lob) as LineOfBusinessEnum),
    [lob]
  );
  const isWorkersComp = lob === LineOfBusinessEnum.WorkersComp.toString();
  const isIME = lob === LineOfBusinessEnum.IME.toString();
  const isDisability = lob === LineOfBusinessEnum.Disability.toString();
  const isHealthReview = lob === LineOfBusinessEnum.HealthReview.toString();
  const isPharmacy = lob === LineOfBusinessEnum.Pharmacy.toString();
  const isImpairmentRatingReview =
    lob === LineOfBusinessEnum.ImpairmentRatingReview.toString();

  function formatPhoneNumber(phone: string): string {
    const cleaned = ('' + phone).replace(/\D/g, '');

    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `(${match[1]}) ${match[2]} - ${match[3]}`;
    }

    return phone;
  }
  return (
    <div className="read-only-content">
      <Render
        condition={
          isWorkersComp ||
          isIME ||
          isDisability ||
          isHealthReview ||
          isPharmacy ||
          isImpairmentRatingReview
        }
      >
        <TitleReadOnly>Date</TitleReadOnly>
        <ReadOnlyField
          id="dueDate"
          label="Due Date & Time"
          forceLabel="Due Date & Time"
          value={
            form.getFieldProps('dueDate').value
              ? formatDate(parseUtcToLocal(form.getFieldProps('dueDate').value) || new Date(), 'g')
              : ''
          }
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="tat"
          label="TAT (Turn Around Time)"
          value={form.getFieldProps('tat').value?.displayName ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="tatPriority"
          label="TAT Priority"
          value={form.getFieldProps('tatPriority').value?.displayName ?? ''}
          schema={ReferralRequestSchema}
        />
        <TitleReadOnly>Requirements</TitleReadOnly>
        <Render condition={!isIME}>
          <ReadOnlyField
            id="jurisditionalMatchRequired"
            label="Jurisdictional Match Required"
            value={
              form.getFieldProps('jurisditionalMatchRequired')?.value
                ? 'Yes'
                : 'No'
            }
            schema={ReferralRequestSchema}
          />
        </Render>
        <Render condition={!isIME}>
          <ReadOnlyField
            id="benefitState"
            label="State of Jurisdiction"
            value={form.getFieldProps('benefitState').value?.displayName ?? ''}
            schema={ReferralRequestSchema}
          />
        </Render>
        <ReadOnlyField
          id="requestedReviewerSpecialty"
          label="Requested Reviewer Specialty"
          value={
            form.getFieldProps('requestedReviewerSpecialty').value
              ?.displayName ?? ''
          }
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="service"
          label="Service"
          value={form.getFieldProps('service').value?.name ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="subService"
          label="subService"
          value={form.getFieldProps('subService').value?.name ?? ''}
          schema={ReferralRequestSchema}
        />
        <ReadOnlyField
          id="numberOfReviews"
          label="Number of Reviews"
          value={form.getFieldProps('numberOfReviews').value?.displayName ?? ''}
          schema={ReferralRequestSchema}
        />
        {form
          .getFieldProps('requestedReviewerSpecialties')
          .value?.map((specialty: any, index: number) => (
            <ReadOnlyField
              key={index}
              id="requestedReviewerSpecialties"
              forceLabel={`Requested Reviewer Specialty ${index + 1}`}
              value={specialty?.displayName ?? ''}
              schema={ReferralRequestSchema}
            />
          ))}
        <TitleReadOnly>Attending Provider(s)</TitleReadOnly>
        <ReadOnlyField
          id="apContactPreference"
          label="AP Contact Preference"
          value={
            form.getFieldProps('apContactPreference').value?.displayName ?? ''
          }
          schema={ReferralRequestSchema}
        />
        {form
          .getFieldProps('attendingProviders')
          .value?.map((provider: any, index: number) => {
            const hasMoreThanOneProvider =
              form.getFieldProps('attendingProviders').value.length > 1;
            return (
              <div key={index}>
                <TitleReadOnly>
                  Attending Provider {hasMoreThanOneProvider ? index + 1 : ''}{' '}
                  Information
                </TitleReadOnly>
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP ${hasMoreThanOneProvider ? index + 1 : ''} Name`}
                  value={provider?.apName ?? ''}
                  forceLabel={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Name`}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Specialty`}
                  value={provider?.apSpecialty?.displayName ?? ''}
                  forceLabel={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Specialty`}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="attendingProviders"
                  label={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Phone Number`}
                  value={formatPhoneNumber(provider?.apPhoneNumber) ?? ''}
                  forceLabel={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Phone Number`}
                  schema={ReferralRequestSchema}
                />
                <ReadOnlyField
                  id="apEmail"
                  label={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Fax or Email`}
                  value={provider?.apEmail ?? ''}
                  forceLabel={`AP ${
                    hasMoreThanOneProvider ? index + 1 : ''
                  } Fax or Email`}
                  schema={ReferralRequestSchema}
                />
              </div>
            );
          })}
      </Render>
    </div>
  );
};
